/**
 * Modal de aviso de errores de integridad detectados durante la generación o
 * el guardado de contenido generado por la IA (historias y conversaciones).
 *
 * Muestra los errores en lenguaje humano y ofrece al editor:
 *   - "Reintentar con IA": llama a un endpoint de pasada correctiva que
 *     pretende arreglar los errores residuales sin descartar el draft.
 *   - "Cerrar": cierra el modal sin descartar nada — el draft sigue editable.
 *
 * No se ofrece "Guardar de todos modos": si el backend rechaza la persistencia
 * por integridad, el editor debe arreglarlo (a mano o con un nuevo retry de
 * IA) antes de guardar.
 */

import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/molecules/Dialog';
import styles from './IntegrityRetryModal.module.scss';

interface IntegrityRetryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** Lista de errores ya humanizados para mostrar al editor. */
  errors: string[];
  /** Callback al pulsar "Reintentar con IA". */
  onRetry: () => void;
  /** Estado del botón Reintentar. Cuando es true, se desactiva y muestra "Reintentando…". */
  retrying?: boolean;
  /** Texto opcional sobre el botón Reintentar (p. ej. "Intento 3"). */
  retryHint?: string;
}

export function IntegrityRetryModal({
  open,
  onOpenChange,
  title = 'Hay actividades inválidas',
  description = 'La IA generó contenido que no pasa la validación de integridad. Puedes pedirle que lo revise sin descartar el resto del draft, o cerrar este aviso y corregirlo a mano.',
  errors,
  onRetry,
  retrying = false,
  retryHint,
}: IntegrityRetryModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={styles.modal}>
        <DialogHeader>
          <DialogTitle className={styles.modal__title}>
            <Icon icon={AlertCircle} size="md" className={styles.modal__icon} />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className={styles.modal__body}>
          {errors.length === 0 ? (
            <p className={styles.modal__empty}>(sin errores adicionales)</p>
          ) : (
            <ul className={styles.modal__list}>
              {errors.map((e, i) => (
                <li key={i} className={styles.modal__item}>
                  {e}
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter className={styles.modal__footer}>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={retrying}
          >
            Cerrar
          </Button>
          <div className={styles.modal__retryBlock}>
            {retryHint && <span className={styles.modal__retryHint}>{retryHint}</span>}
            <Button variant="primary" onClick={onRetry} disabled={retrying}>
              {retrying ? 'Reintentando…' : 'Reintentar con IA'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
